import { getToken, setToken, refreshOnce } from "./api";

// One shared live connection for the whole app: every screen subscribes to it instead of opening its own socket.
const subs = new Set();
const BATCH_MS = 300, POLL_MS = 30000, MAX_BACKOFF = 30000;
let ws = null, retryT = null, pollT = null, flushT = null, stableT = null, tries = 0, opened = false, running = false;
let pending = new Set();

export function wsUrl() {
  const base = (process.env.REACT_APP_BACKEND_URL || "").trim().replace(/\/+$/, "");
  const origin = base || window.location.origin;
  return `${origin.replace(/^http/, "ws")}/api/ws`;
}

function flush() {
  flushT = null;
  const types = pending;
  pending = new Set();
  subs.forEach((s) => { if (!s.wants || [...types].some((t) => t === "resync" || s.wants(t))) s.cb.current(types); });
}

function queue(type) {
  pending.add(type);
  if (!flushT) flushT = setTimeout(flush, BATCH_MS);
}

function startPoll() { if (!pollT) pollT = setInterval(() => queue("resync"), POLL_MS); }
function stopPoll() { clearInterval(pollT); pollT = null; }

function retry() {
  const cap = Math.min(MAX_BACKOFF, 1000 * 2 ** tries++);
  retryT = setTimeout(connect, cap / 2 + Math.random() * (cap / 2));
}

function connect() {
  retryT = null;
  if (!running || !getToken()) return;
  try { ws = new WebSocket(`${wsUrl()}?token=${encodeURIComponent(getToken())}`); } catch { startPoll(); retry(); return; }
  // backoff resets only once a socket has stayed up, so a server that accepts then closes (4401) cannot cause a storm
  ws.onopen = () => { stableT = setTimeout(() => { tries = 0; }, 10000); stopPoll(); if (opened) queue("resync"); opened = true; };
  ws.onmessage = (e) => {
    let type = "event";
    try { type = JSON.parse(e.data).type || type; } catch { /* not JSON */ }
    if (type !== "ping") queue(type);
  };
  ws.onclose = async (e) => {
    ws = null;
    clearTimeout(stableT);
    if (!running) return;
    startPoll();
    if (e.code === 4403) return;
    if (e.code === 4401) {
      const t = await refreshOnce();
      if (!t) return;
      setToken(t);
    }
    retry();
  };
}

function start() { running = true; opened = false; tries = 0; connect(); }
function stop() {
  running = false;
  clearTimeout(retryT); clearTimeout(flushT); clearTimeout(stableT); stopPoll();
  retryT = flushT = null; pending = new Set();
  if (ws) { ws.onclose = null; ws.close(); ws = null; }
}

export function subscribe(entry) {
  subs.add(entry);
  if (subs.size === 1) start();
  return () => { subs.delete(entry); if (!subs.size) stop(); };
}
